import React from 'react';
import { Alert, Button, IconButton, MenuItem, TextField, Tooltip } from '@mui/material';
import { Check, GitMerge, Plus, Trash2 } from 'lucide-react';
import PathField from './PathField';
import styles from './KgCombinedFieldsStep.module.scss';
import {
  COMBINED_USES,
  combinedDraftProblems,
  combinedSavedPath,
  coverageTone,
  emptyCombinedDraft,
  formatCoverage,
  formatNumber,
  slugifyFacetName,
  truncateValue,
} from './kgUtils';

/**
 * The "Combined fields" step of the create wizard.
 *
 * On a multi-source export the same thing lives under a different name per
 * source — the email is `traits.email` on one, `contact_email` on another. Each
 * column alone covers a third of the records and reads as a weak facet; combined
 * they cover all of them. The server spots these and proposes them in
 * `suggestions.combined_fields[]`; this step shows them ticked where it
 * recommends, lets the author add or drop columns, and lets them build their own.
 *
 * Combining takes the FIRST candidate that has a value, in the order listed —
 * values are never glued together.
 */
const KgCombinedFieldsStep = ({ drafts, onChange, fields, namespace, inspectedCount }) => {
  const problems = combinedDraftProblems(drafts);

  const update = (id, updater) =>
    onChange(
      drafts.map((draft) => {
        if (draft.id !== id) return draft;
        const next = { ...draft, candidates: draft.candidates.map((c) => ({ ...c })) };
        updater(next);
        return next;
      })
    );

  /** An edit is a decision to use it — so it also switches the field on. */
  const edit = (id, updater) =>
    update(id, (draft) => {
      updater(draft);
      draft.enabled = true;
    });

  const fieldByPath = new Map((fields || []).map((f) => [f.path, f]));

  const addCandidate = (id, path) => {
    if (!path) return;
    edit(id, (draft) => {
      if (draft.candidates.some((c) => c.path === path)) {
        draft.candidates = draft.candidates.map((c) => (c.path === path ? { ...c, ticked: true } : c));
        return;
      }
      const field = fieldByPath.get(path);
      draft.candidates.push({
        path,
        populatedRecords: 0,
        coveragePct: field?.coveragePct || 0,
        examples: field?.values || [],
        recommended: false,
        reason: null,
        ticked: true,
        added: true,
      });
      // A new combined field with no name yet is named after its first column.
      if (draft.custom && !draft.name) draft.name = slugifyFacetName(field?.leaf || path);
    });
  };

  return (
    <div className={styles.step}>
      <p className={styles.lead}>
        Some things in your data live under a different name depending on where the record came
        from. Combining those columns gives one field that covers every record — it takes the
        first of the listed columns that has a value, and never joins values together. Suggested
        combinations are switched on where recommended; change anything, or build your own.
      </p>

      {drafts.length === 0 && (
        <div className={styles.empty}>No combinations were suggested for this data.</div>
      )}

      {drafts.map((draft) => {
        const draftProblems = problems[draft.id] || [];
        const ticked = draft.candidates.filter((c) => c.ticked).length;
        const gain = draft.populatedRecords - draft.bestSinglePopulatedRecords;
        return (
          <div
            key={draft.id}
            className={`${styles.card} ${draft.enabled ? styles.cardOn : styles.cardOff}`}
          >
            <div className={styles.cardHead}>
              <button
                type="button"
                className={`${styles.box} ${draft.enabled ? styles.boxOn : ''}`}
                onClick={() => update(draft.id, (d) => { d.enabled = !d.enabled; })}
                aria-pressed={draft.enabled}
                aria-label={draft.enabled ? 'Do not combine' : 'Combine'}
              >
                {draft.enabled && <Check size={12} strokeWidth={3} />}
              </button>
              <GitMerge size={15} className={styles.headIcon} />
              <TextField
                size="small"
                label="Field name"
                value={draft.name}
                onChange={(e) => edit(draft.id, (d) => { d.name = e.target.value; })}
                onBlur={(e) =>
                  e.target.value &&
                  update(draft.id, (d) => { d.name = slugifyFacetName(e.target.value); })
                }
                InputProps={{ className: styles.mono }}
                className={styles.nameField}
              />
              <TextField
                select
                size="small"
                label="Use it as"
                value={draft.useAs}
                onChange={(e) => edit(draft.id, (d) => { d.useAs = e.target.value; })}
                className={styles.useField}
              >
                {COMBINED_USES.map((use) => (
                  <MenuItem key={use.value} value={use.value}>
                    {use.label}
                  </MenuItem>
                ))}
              </TextField>
              {draft.custom ? (
                <IconButton
                  size="small"
                  aria-label="Remove this combined field"
                  onClick={() => onChange(drafts.filter((d) => d.id !== draft.id))}
                >
                  <Trash2 size={14} />
                </IconButton>
              ) : (
                draft.recommended && <em className={styles.tag}>recommended</em>
              )}
            </div>

            {!draft.enabled && (
              <span className={styles.offNote}>
                Not used. Tick the box or change anything below to use this combined field.
              </span>
            )}

            <div className={styles.meta}>
              <code>{combinedSavedPath(draft, namespace) || `${namespace}.…`}</code>
              {/* The whole argument for combining, in one line. */}
              {!draft.custom && draft.populatedRecords > 0 && (
                <span>
                  covers <strong>{formatNumber(draft.populatedRecords)}</strong>
                  {inspectedCount ? ` of ${formatNumber(inspectedCount)}` : ''} records
                  {gain > 0 && (
                    <>
                      {' '}instead of {formatNumber(draft.bestSinglePopulatedRecords)} with the best
                      single column
                    </>
                  )}
                </span>
              )}
              {draft.custom && <span>coverage is measured on the next step</span>}
            </div>

            <div className={styles.candidates}>
              <span className={styles.candidatesLabel}>
                Read from, in this order — the first with a value wins
              </span>
              {draft.candidates.map((candidate, index) => (
                <div
                  key={candidate.path}
                  className={`${styles.candidate} ${candidate.ticked ? '' : styles.candidateOff}`}
                >
                  <button
                    type="button"
                    className={`${styles.box} ${candidate.ticked ? styles.boxOn : ''}`}
                    onClick={() =>
                      edit(draft.id, (d) => {
                        d.candidates[index].ticked = !d.candidates[index].ticked;
                      })
                    }
                    aria-pressed={candidate.ticked}
                      >
                    {candidate.ticked && <Check size={12} strokeWidth={3} />}
                  </button>
                  <div className={styles.candidateMain}>
                    <span className={styles.candidateName}>
                      <code>{candidate.path}</code>
                      <span className={styles[coverageTone(candidate.coveragePct)]}>
                        {formatCoverage(candidate.coveragePct)}
                      </span>
                      {candidate.examples.length > 0 && (
                        <span className={styles.examples}>
                          {candidate.examples.slice(0, 2).map((v) => truncateValue(v, 26)).join(' · ')}
                        </span>
                      )}
                    </span>
                    {/* Why the server left it out — shown whether or not the
                        author has since ticked it. */}
                    {candidate.reason && (
                      <span className={styles.reason}>{candidate.reason}</span>
                    )}
                  </div>
                  {candidate.added && (
                    <Tooltip title="Remove">
                      <IconButton
                        size="small"
                        onClick={() =>
                          update(draft.id, (d) => {
                            d.candidates.splice(index, 1);
                          })
                        }
                      >
                        <Trash2 size={13} />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              ))}

              {(
                <div className={styles.addRow}>
                  <PathField
                    value=""
                    onChange={(v) => addCandidate(draft.id, v)}
                    fields={fields}
                    label={draft.candidates.length ? 'Add another column' : 'Pick a column'}
                    placeholder="Search the columns in your data"
                    excludeLists
                  />
                </div>
              )}
            </div>

            {draft.enabled && draftProblems.length > 0 && (
              <Alert severity="warning" className={styles.alert}>
                {draftProblems.join(' ')}
              </Alert>
            )}
            {draft.enabled && ticked > 0 && draftProblems.length === 0 && draft.useAs === 'identity' && (
              <span className={styles.note}>
                Becomes the row identity: records with the same {draft.name || 'value'} are one
                record, and each push overwrites the previous one.
              </span>
            )}
          </div>
        );
      })}

      <Button
        size="small"
        variant="text"
        startIcon={<Plus size={14} />}
        onClick={() => onChange([...drafts, emptyCombinedDraft()])}
        className={styles.createBtn}
      >
        Create a combined field
      </Button>
    </div>
  );
};

export default KgCombinedFieldsStep;
